import { Component, Fragment, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/components/ui/utils';
import { requestReload } from '@/pwa/update';

interface Props {
  children: ReactNode;
  /** Rendered instead of children after an uncaught render error. */
  fallback?: ReactNode;
}

interface State {
  /** The caught error, kept so the fallback can show it in DEV. */
  error: Error | null;
  /** Where it was thrown. Rendered in DEV only — see ErrorScreen. */
  componentStack: string | null;
  /**
   * Bumped by `reset()`. It keys the children, so a retry REMOUNTS the subtree
   * rather than re-rendering the state that just threw — a component that
   * crashed on a bad prop or a half-built store would otherwise crash again on
   * the same frame and the button would read as dead.
   */
  attempt: number;
}

/**
 * Shell error boundary (09-quality.md §6). Never renders error internals that
 * could contain tokens or customer data in a production build; details go to
 * the (scrubbed) console and, in DEV only, behind a closed <details>.
 *
 * This is the LAST resort — it is mounted above the providers (providers.tsx)
 * and above each route group's shell (router.tsx), so by the time it renders,
 * the chrome is already gone. Page-level failures are caught below the shell by
 * `RouteErrorFallback`, which keeps the navigation the user needs to escape.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null, attempt: 0 };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    // A throw is not required to be an Error — `throw 'nope'` and a rejected
    // non-Error both land here — and the fallback reads `.name`/`.message`.
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null });
    // Frontend monitoring hookup happens in V1-10; keep messages sanitized.
    console.error('[sg-console] Uncaught render error', error.name, info.componentStack ?? '');
  }

  private reset = (): void => {
    this.setState((prev) => ({ error: null, componentStack: null, attempt: prev.attempt + 1 }));
  };

  render(): ReactNode {
    const { error, componentStack, attempt } = this.state;
    if (error) {
      return (
        this.props.fallback ?? (
          <ErrorScreen
            scope="app"
            title="Something went wrong"
            error={error}
            detail={componentStack}
            onRetry={this.reset}
          >
            The back office could not finish drawing this screen. Trying again rebuilds it from
            scratch; if it fails the same way, reload the page.
          </ErrorScreen>
        )
      );
    }
    return <Fragment key={attempt}>{this.props.children}</Fragment>;
  }
}

export interface ErrorScreenProps {
  /**
   * `app` — nothing else survived, so it fills the viewport and paints its own
   * background. `page` — the shell is still standing, so it fills the content
   * area under a topbar and sidebar the user can still navigate with.
   */
  scope: 'app' | 'page';
  title: string;
  /** The honest one-liner: what is broken, and what the buttons will do. */
  children: ReactNode;
  error?: Error | null;
  /** Component stack or stack trace. DEV only, never rendered in production. */
  detail?: string | null;
  onRetry?: () => void;
  retryLabel?: string;
}

/**
 * The fallback both boundaries render, in the design system rather than in
 * inline styles — this is the one screen a user only ever sees on a bad day,
 * which is exactly when the product should not look unfinished.
 *
 * Deliberately free of router and store hooks: `ErrorBoundary` mounts it from
 * providers.tsx, outside the RouterProvider, where those hooks would throw
 * inside the handler for a crash that has already happened.
 */
export function ErrorScreen({
  scope,
  title,
  children,
  error,
  detail,
  onRetry,
  retryLabel = 'Try again',
}: ErrorScreenProps) {
  const Heading = scope === 'app' ? 'h1' : 'h2';
  return (
    <div
      role="alert"
      data-slot="error-screen"
      className={cn(
        'grid place-items-center px-4 py-10',
        scope === 'app' ? 'min-h-dvh bg-background' : 'min-h-full',
      )}
    >
      <div className="w-full max-w-md text-center">
        <p className="micro">Error</p>
        <Heading className="mt-1 text-balance font-display text-title text-text">{title}</Heading>
        <p className="mt-2 text-body font-book text-muted">{children}</p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {onRetry ? <Button onClick={onRetry}>{retryLabel}</Button> : null}
          {/* Through the update seam, not a bare reload: when the crash was a
              stale route chunk after a deploy, a plain reload on a page the
              service worker controls serves the same old shell again. */}
          <Button variant="outline" onClick={() => requestReload()}>
            Reload the page
          </Button>
        </div>
        {import.meta.env.DEV && error ? (
          <details className="mt-6 rounded-card border border-border bg-well p-3 text-left">
            <summary className="cursor-pointer text-label font-mid text-text">
              Error detail (dev only)
            </summary>
            <p className="mt-2 text-caption text-danger-text">
              {error.name}: {error.message}
            </p>
            {detail ? (
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-caption font-book text-muted">
                {detail}
              </pre>
            ) : null}
          </details>
        ) : null}
      </div>
    </div>
  );
}
